import { Column, Entity, Index, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { SupportedPaymentMethodTypeFeeStateEntity } from './supported-payment-method-type-fee-state.entity';
import { SupportedPaymentMethodTypeStateEntity } from './supported-payment-method-type-state.entity';

@Index('IDX_paymentMethodType', ['paymentMethodType'], {})
@Index(
	'IDX_paymentMethodType_deletedDate',
	['paymentMethodType', 'deletedDate'],
	{},
)
@Entity('supported_payment_method_type')
export class SupportedPaymentMethodTypeEntity extends BaseEntity {
	@Column('varchar', { name: 'paymentMethodType', length: 64 })
	public paymentMethodType!: string;

	@Column('enum', { name: 'processingFeeType', enum: ['%', '$'] })
	public processingFeeType!: '%' | '$';

	@Column('decimal', {
		name: 'processingFee',
		precision: 10,
		scale: 2,
		default: 0,
	})
	public processingFee!: number;

	/**
	 * PD-15688 — which of the customer's addresses decides the state used to
	 * resolve this type's fee (the fee-state list and any per-state rate
	 * override from PD-15583).
	 *
	 * Nullable, and NULL means 'shipping'. Every configuration that predates
	 * this column therefore keeps its existing behaviour with no backfill,
	 * and the column is purely additive so database sync creates it — no
	 * migration required.
	 *
	 * Card-surcharge rules are read differently by different sellers: some
	 * read them against where the buyer takes delivery, others against the
	 * address on the instrument being charged. Rather than pick one, the
	 * tenant states which one they operate under.
	 */
	@Column('enum', {
		name: 'feeAddressBasis',
		enum: ['shipping', 'billing'],
		nullable: true,
	})
	public feeAddressBasis!: 'shipping' | 'billing' | null;

	/**
	 * PD-16010 — whether this type may be used on the ecommerce checkout, and
	 * whether it may be used to pay an invoice (CRM and portal alike).
	 *
	 * Which types exist has always been one tenant-wide list, with each surface
	 * then narrowing it through an allowlist written into the code. A tenant
	 * that wanted ACH for paying invoices but not at checkout had no way to say
	 * so: turning ACH off turned it off everywhere.
	 *
	 * Modeled per type rather than as an ACH-specific setting so the same
	 * control answers the next request of this shape — Company Credit at
	 * checkout only, say — without another flag.
	 *
	 * Both default to ON, so every configuration that predates them keeps
	 * today's behaviour with no manual setup: the columns are purely additive,
	 * and database sync's ADD COLUMN carries the default onto existing rows.
	 *
	 * One flag covers the CRM and portal invoice-payment screens together;
	 * there is no use case for splitting them today.
	 *
	 * MySQL hands these back as 0/1 rather than booleans, so read them for
	 * truthiness (or through `isPaymentTypeAvailableOnSurface`) — never with
	 * `=== true`, which is false for a switched-on row.
	 */
	@Column('tinyint', {
		name: 'availableAtCheckout',
		width: 1,
		default: () => "'1'",
	})
	public availableAtCheckout!: boolean;

	@Column('tinyint', {
		name: 'availableForInvoicePayment',
		width: 1,
		default: () => "'1'",
	})
	public availableForInvoicePayment!: boolean;

	@OneToMany(
		() => SupportedPaymentMethodTypeFeeStateEntity,
		(supportedPaymentMethodTypeFeeState) =>
			supportedPaymentMethodTypeFeeState.fkSupportedPaymentMethodType,
		{},
	)
	public supportedPaymentMethodTypeFeeStates!: Array<SupportedPaymentMethodTypeFeeStateEntity>;

	@OneToMany(
		() => SupportedPaymentMethodTypeStateEntity,
		(supportedPaymentMethodTypeState) =>
			supportedPaymentMethodTypeState.fkSupportedPaymentMethodType,
		{},
	)
	public supportedPaymentMethodTypeStates!: Array<SupportedPaymentMethodTypeStateEntity>;
}
